import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '~/utils';

export const badgeVariants = cva(
    'aria-invalid:border-destructive aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden whitespace-nowrap rounded-md border px-2 py-0.5 text-xs font-medium transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
    {
        variants: {
            variant: {
                default:
                    'border-transparent bg-primary text-primary-foreground text-white [a&]:hover:bg-primary/90',
                secondary:
                    'border-transparenttext-white bg-secondary text-secondary-foreground [a&]:hover:bg-secondary/90',
                destructive:
                    'border-transparent bg-destructive text-white focus-visible:ring-destructive/20 dark:bg-destructive/70 dark:focus-visible:ring-destructive/40 [a&]:hover:bg-destructive/90',
                outline: 'text-foreground [a&]:hover:bg-accent [a&]:hover:text-accent-foreground',
                positive:
                    'border-transparent bg-turqoise-50 text-turqoise-500 [a&]:hover:bg-turqoise-100',
                passive: 'border-transparent bg-blue-100 text-blue-500 [a&]:hover:bg-blue-200',
                pending:
                    'border-transparent bg-yellow-100 text-yellow-600 [a&]:hover:bg-yellow-200',
                negative:
                    'border-transparent bg-orange-50 text-orange-700 [a&]:hover:bg-orange-100',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

export interface BadgeProps
    extends React.ComponentProps<'span'>, VariantProps<typeof badgeVariants> {
    asChild?: boolean;
}

export const Badge: React.FC<BadgeProps> = ({ className, variant, asChild = false, ...props }) => {
    const Comp = asChild ? Slot : 'span';

    return (
        <Comp data-slot="badge" className={cn(badgeVariants({ variant }), className)} {...props} />
    );
};
